/**
 * useRowNavigation — Arrow-key navigation through table rows while detail panel is open
 *
 * When the entity detail panel is open, ArrowUp/ArrowDown cycle through rows
 * in the Tabulator table, updating the panel content live.
 *
 * Usage:
 *   const nav = useRowNavigation({
 *     data,
 *     selectedEntity: selectedItem,
 *     panelOpened: drawerOpened,
 *     onNavigate: (entity) => { setSelectedItem(entity); },
 *     tabulatorRef: tabulatorInstance,
 *   });
 *   <EntityDetailPanel navigation={nav} ... />
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import type { RowNavigationState } from './types';

interface UseRowNavigationOptions {
  /** Full dataset (filtered if applicable) */
  data: PrimitiveItem[];
  /** Currently selected entity */
  selectedEntity: PrimitiveItem | null;
  /** Whether the detail panel is open */
  panelOpened: boolean;
  /** Callback when navigating to a new entity */
  onNavigate: (entity: PrimitiveItem) => void;
  /** Optional Tabulator instance ref for scroll-into-view */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tabulatorRef?: React.RefObject<any | null>;
}

export function useRowNavigation({
  data,
  selectedEntity,
  panelOpened,
  onNavigate,
  tabulatorRef,
}: UseRowNavigationOptions): RowNavigationState {
  // Find current index from selected entity
  const currentIndex = useMemo(() => {
    if (!selectedEntity) return 0;
    const idx = data.findIndex(item => item.id === selectedEntity.id);
    return idx >= 0 ? idx : 0;
  }, [data, selectedEntity]);

  const totalRows = data.length;

  const navigateTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, data.length - 1));
    const entity = data[clamped];
    if (entity) {
      onNavigate(entity);
      // Scroll Tabulator row into view
      try {
        tabulatorRef?.current?.scrollToRow(entity.id, 'center', false);
        tabulatorRef?.current?.selectRow(entity.id);
      } catch {
        // Tabulator may not support scrollToRow or selectRow
      }
    }
  }, [data, onNavigate, tabulatorRef]);

  const goToPrevious = useCallback(() => navigateTo(currentIndex - 1), [currentIndex, navigateTo]);
  const goToNext = useCallback(() => navigateTo(currentIndex + 1), [currentIndex, navigateTo]);
  const goToFirst = useCallback(() => navigateTo(0), [navigateTo]);
  const goToLast = useCallback(() => navigateTo(data.length - 1), [data.length, navigateTo]);

  // Keyboard listeners when panel is open
  useEffect(() => {
    if (!panelOpened) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          goToFirst();
        } else {
          goToPrevious();
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) {
          goToLast();
        } else {
          goToNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panelOpened, goToPrevious, goToNext, goToFirst, goToLast]);

  return {
    currentIndex,
    totalRows,
    goToPrevious,
    goToNext,
    goToFirst,
    goToLast,
  };
}
